/*
 * 「我的」数据接口。`/api/v1/app/me/*`
 *
 * 与 `auth-api.ts` 同一层：只搬数据，不做判断，也不做兜底。
 *
 * ## 路径为什么全都带 `/app`
 *
 * 不是命名风格，是会话会不会断的问题。Refresh Cookie 的作用域被服务端收窄到了
 * `/api/v1/app`（缩小凭据泄漏面），这个前缀之外的请求带不上它，
 * Access Token 15 分钟一到就只能 401。表现是「用着用着突然要重新登录」，
 * 而且只在打开某几个页面时出现。服务端 `app-auth.guard.ts` 把这条写成了守卫的
 * 使用前提，客户端这边必须对齐。
 *
 * ## 不变量
 *
 *   - **不传任何资源 ID。** userId 来自 Session，characterId 由服务端从
 *     Session 的 productId 反查。客户端能声明 characterId 就等于多角色上线后
 *     可以读别人的关系。所以这些函数除了翻页游标之外没有参数。
 *   - **不在这一层做兜底。** 读不到就抛 ApiError，由页面决定怎么呈现。
 *     在这里 `?? 0` 会让「服务端没给」和「服务端给了 0」永远分不开。
 */

import type { ConversationMessage } from "@dreamverse/contracts";
import { apiRequest } from "./api-client";
import type { TodayChannelEncounterPlatform } from "./channel-encounter-api";

/* -------------------------------------------------------------------------- */
/* 我的资料                                                                    */
/* -------------------------------------------------------------------------- */

export interface ProfileView {
  displayName: string;
  /** 已本地化的性别文案。没填是 null。 */
  gender: string | null;
  /**
   * 性别的原始枚举值，编辑表单回填用。
   *
   * ⚠ **不要拿 `gender` 那个中文标签反查枚举。** 标签随时可能改文案，
   * 改了之后回填会静默失效——表现是「打开表单性别变成未选中」，
   * 用户一保存就把自己填过的值清了。
   */
  genderCode: "male" | "female" | "other" | "undisclosed" | null;
  /** 生日 `YYYY-MM-DD`。服务端直接给字符串，避免时区把日期挪一天。 */
  birthday: string | null;
  /** 常驻城市。 */
  homeCity: string | null;
  /** 自我介绍。服务端挂在关系上——它是说给某个角色听的。 */
  bio: string | null;
  /** 脱敏手机号，如 `180****4178`。没绑定是 null。 */
  maskedPhone: string | null;
  /** 用户对角色的期待。旧版界面上叫「角色期望」。 */
  wish: string | null;
  /** 用户希望被怎么称呼。 */
  preferredName: string | null;
  wechatBound: boolean;
  joinedAt: string;
  /**
   * 哪些字段可改。由服务端下发。
   *
   * `bio` / `wish` / `preferredName` 挂在关系上，没有角色上下文时不在列表里——
   * 那种情况下写接口会返回 `NO_CHARACTER_CONTEXT`，前端提前禁用比
   * 让用户填完再被拒好。
   */
  editableFields: string[];
}

/**
 * 改资料。只送要改的键。
 *
 * ⚠ **不要为了「保险」把整个表单都送上去。** 服务端按 PATCH 语义处理：
 * 缺席 = 不动，`""` = 清空。整表提交会把当时没渲染的字段一并清掉。
 */
export interface ProfilePatch {
  displayName?: string;
  gender?: "male" | "female" | "other" | "undisclosed" | null;
  /** `YYYY-MM-DD`，null 清空。 */
  birthday?: string | null;
  homeCity?: string;
  bio?: string;
  wish?: string;
  preferredName?: string;
}

/* -------------------------------------------------------------------------- */
/* 羁绊与灵力                                                                  */
/* -------------------------------------------------------------------------- */

export interface LedgerEntry {
  id: string;
  at: string;
  /** 负数为扣除。 */
  delta: number;
  balanceAfter: number;
  reason: string;
  reasonKey: string;
}

export interface RelationshipView {
  /**
   * 关系是否已经建立。
   *
   * 注册完还没说过一句话时为 false，此时下面的数字全是零值。
   * **必须据此区分「还没开始」与「灵力被扣光了」**——两者都是 0，但含义相反。
   */
  started: boolean;
  startedAt: string | null;
  lastInteractionAt: string | null;

  stageLabel: string;
  stageKey: string;
  nextStageLabel: string | null;
  /** 还差多少**等级**进入下一阶段。已是最高档为 0。 */
  levelsToNextStage: number;

  /** 羁绊等级。服务端的权威状态，只增不减。 */
  level: number;
  /**
   * **当前等级内**已积累的羁绊，不是累计总量。
   *
   * 升级时会减去该级消耗重新计数，所以它可以变小——那是升级不是扣减，
   * 界面上不要把它当余额来表达。配合 `bondToNextLevel` 使用。
   */
  bond: number;
  /** 升到下一级总共需要多少羁绊，即本级进度条的满格值。 */
  bondForThisLevel: number;
  /** 还差多少羁绊升下一级。满级为 0。 */
  bondToNextLevel: number;
  /**
   * 本级进度，0~1。
   *
   * 服务端算好下发，**前端不反推**——旧版 `ExpLevel.tsx` 内联了同一条等级曲线，
   * 与服务端各算各的，调参时必然分叉。
   */
  levelProgress: number;

  affinity: number;
  powerBalance: number;
  /**
   * 灵力表盘的填充比例，0~1。
   *
   * ⚠ **不是「余额 / 上限」**——灵力**没有上限**（`POWER_BALANCE_CAP === null`，
   * 余额不过期也不清零）。这是服务端拿一条**显示参考线**
   * （`POWER_GAUGE_FULL`，当前 100，等于新用户礼物）算的，超出即截断。
   * 表盘的用途是快用完时预警，满格之上的差别对用户没有决策价值。
   *
   * ⚠ **前端不许自己拿 `powerBalance` 去除一个内联的常数。** 与 `levelProgress`
   * 同一条规矩：旧版把等级曲线和称号阈值内联在前端，服务端一改就静默错位。
   */
  powerProgress: number;
  /**
   * 用户希望被怎么称呼。没说过是 null，**不要回落成昵称**。
   *
   * 这里只读不问。「要不要给称呼输入框」由 `ChatEnterView.askedPreferredName`
   * 下发——判据是「她这一轮真的问了」，本接口不知道当前那轮对话说了什么。
   */
  preferredName: string | null;

  ledger: LedgerEntry[];
}

/* -------------------------------------------------------------------------- */
/* 记忆中心                                                                    */
/* -------------------------------------------------------------------------- */

export interface MemoryDiary {
  id: string;
  /** `YYYY-MM-DD`。服务端直接给字符串，避免时区把日期挪一天。 */
  date: string;
  title: string | null;
  excerpt: string;
  tags: string[];
}

/** 打开回忆页面时，服务端为缺失摘要/日记安排的后台自愈状态。 */
export interface MemoryRecovery {
  queued: number;
  pending: number;
}

export interface MemoryCenter {
  diaries: MemoryDiary[];
  /** 有回忆内容的日期总数；可能来自关系日记，也可能只有站外相遇。 */
  diaryTotal: number;
  /**
   * 这一页之后还有没有。
   *
   * ⚠ **不要拿第一页的这个值当「还能不能再翻」用。** 它描述的是本次请求
   * 之后还有没有，翻到底之后第一页的那个值仍然是 true。
   * 判断还能不能翻要用「已加载条数 < diaryTotal」。
   */
  hasMore: boolean;
  recovery: MemoryRecovery;
}

/* -------------------------------------------------------------------------- */
/* 回忆点滴：某一天                                                             */
/* -------------------------------------------------------------------------- */

/**
 * 某一天里的一段对话。
 *
 * ⚠ **`id` 是这段对话的「记忆碎片」id，不是会话 id。** 一次长对话会按上下文
 * 折叠点切成多段，每段各有自己的标题、时间范围与记忆碎片，用户点开的是那一段。
 * 拿 `conversationId` 当键的话，长对话会出现两个标题不同、点进去一模一样的条目。
 * `conversationId` 只作说明用，不参与路由。
 */
export interface DayConversation {
  id: string;
  conversationId: string;
  /** 20 字内的对话标题。总结失败时是 null。 */
  title: string | null;
  startedAt: string;
  /** 这段对话的最后一条消息。迁移来的旧数据可能是 null。 */
  finishedAt: string | null;
  messageCount: number;
}

/** 一天的站外互动摘要；原始消息在同一个 DayView 的 channelPlatforms 中。 */
export interface ChannelEncounterMemoryView {
  id: string;
  title: string;
  summary: string;
  tags: string[];
  startedAt: string;
  finishedAt: string;
  providerKeys: string[];
  endpointCount: number;
  messageCount: number;
}

export interface DayView {
  /** `YYYY-MM-DD`，回显请求的那一天。 */
  date: string;
  /**
   * 那天的心情日记。**null 是正常态，不是错误**——日记由夜里的定时任务补写，
   * 当天白天来看必然还没有。此时照常列对话，只是没有日记可看。
   */
  diary: MemoryDiary | null;
  /** 按开始时间**正序**。外层日记列表是倒序，进到某一天之后按发生顺序重走一遍。 */
  conversations: DayConversation[];
  /** 当天已绑定身份在其他平台留下的原始对话。 */
  channelPlatforms: readonly TodayChannelEncounterPlatform[];
  /** 夜间任务从上述站外对话整理出的记忆碎片；未生成或没有站外消息时为 null。 */
  channelMemory: ChannelEncounterMemoryView | null;
  recovery: MemoryRecovery;
  /** 服务端权威价格；前端确认文案不得内联数字。 */
  diaryRewritePowerCost: number;
}

/* -------------------------------------------------------------------------- */
/* 回忆点滴：一段对话                                                           */
/* -------------------------------------------------------------------------- */

/** 记忆碎片：她从这段对话里记下了什么。 */
export interface ConversationMemoryView {
  id: string;
  conversationId: string;
  title: string | null;
  summary: string | null;
  /** 触发这段回忆的关键词。 */
  tags: string[];
  startedAt: string;
  finishedAt: string | null;
}

export interface ConversationDetail {
  memory: ConversationMemoryView;
  /** 这一段的消息，按 sequence 升序。 */
  messages: ConversationMessage[];
  /** 服务端权威价格；前端确认文案不得内联数字。 */
  memoryRegenerationPowerCost: number;
}

export interface PaidRegenerationResult {
  status: "written";
  powerCost: number;
  balanceAfter: number;
  charged: boolean;
}

/* -------------------------------------------------------------------------- */
/* 设置                                                                        */
/* -------------------------------------------------------------------------- */

export interface SettingsView {
  /**
   * 允许把对话提炼成记忆。
   *
   * ⚠ 这一项**缺记录时默认为开**，服务端把「从来没表过态」与「拒绝」分开了。
   * 此前它读的是「有没有一条 granted 的 Consent」，而全库真实账号一行都没有，
   * 于是每个人的设置页都显示「关闭」而记忆一直在写——两个方向同时不符。
   */
  memoryEnabled: boolean;
  /**
   * 恒 false，且**不是用户的选择**——`ConsentKind` 枚举里还没有这一项。
   *
   * 界面上已经没有对应的开关了（只有一个梦灵时没有可共享的对象，
   * 摆一个永远关着又点不动的开关是噪音）。字段留着是因为服务端还在下发它，
   * 第二个梦灵上线时前后端一起补。
   */
  crossCharacterSharing: boolean;
  /**
   * 允许她在你打开时先说话（进入式开场 + 续场）。
   *
   * **不是推送通知。** 推送是另一件事，还没上线，到时候会是单独一个开关。
   */
  proactiveGreeting: boolean;
  /**
   * 安静时间段。**界面上已经没有它了**，字段保留是因为服务端还在下发。
   *
   * 撤掉的理由见 `SettingsPage.tsx` 顶部：它约束的是推送式问候，而推送不存在；
   * 进入式开场按设计不受它约束。也就是说她只在你打开之后才开口，
   * 这个设置当下无论设成什么都不改变任何行为。
   */
  quietHours: { from: string; to: string } | null;
  /** 安静时间当前有没有真的约束到什么。恒 false，见上。 */
  quietHoursEnforced: boolean;
  /** 由服务端说了算，后端上线或下线写能力不必同时发前端。 */
  editable: boolean;
  /**
   * 结构上还不支持的开关。当前只有 `crossCharacterSharing`，而它已经没有
   * 界面表示了，所以这个数组暂时没有消费方——加新开关时据它给出准确的
   * 禁用理由，而不是把服务端的「做不到」显示成用户的选择。
   */
  unsupported: string[];
}

/**
 * 改设置。
 *
 * 两项没有列在这里，但**服务端都收**：
 *
 *   `crossCharacterSharing`  `ConsentKind` 枚举里还没有那一项，送上去会 400。
 *   `quietHours`             服务端存得下，但界面已经撤掉了（见 `SettingsView`）。
 *
 * 前端类型里不留它们，是为了别让下一个人以为界面上有对应的控件。
 * 推送（阶段 7）落地时把 `quietHours` 加回来。
 */
export interface SettingsPatch {
  memoryEnabled?: boolean;
  proactiveGreeting?: boolean;
}

/** 只表示用户点过一次性提醒的“我知道了”，不表示实际使用过功能。 */
export interface CostNoticeAcknowledgments {
  chatImagePower: boolean;
  messageVoicePower: boolean;
}

export type CostNoticeKey = "chat_image_power" | "message_voice_power";

/* -------------------------------------------------------------------------- */
/* 会话历史                                                                    */
/* -------------------------------------------------------------------------- */

export interface ConversationSummary {
  id: string;
  /** 会话总结产出的标题。还没总结过是 null。 */
  title: string | null;
  summary: string | null;
  startedAt: string;
  lastMessageAt: string | null;
  messageCount: number;
  /** 这段对话让好感变化了多少。没总结过是 null，**不是 0**。 */
  affinityDelta: number | null;
  tags: string[];
}

export interface ConversationList {
  conversations: ConversationSummary[];
  total: number;
  hasMore: boolean;
}

/* -------------------------------------------------------------------------- */
/* 消息检索                                                                     */
/* -------------------------------------------------------------------------- */

/** 一条命中的消息。 */
export interface MessageSearchHit {
  id: string;
  conversationId: string;
  /**
   * 这条消息所属的**记忆碎片** id，用来跳到 `/memories/:date/:memoryId`。
   *
   * null 表示那段还没总结过（刚聊完不久），此时只能跳到 `/memories/:date`。
   * **不要拿 `conversationId` 顶上去**——那两个是不同的东西，路由那边要的是碎片 id。
   */
  memoryId: string | null;
  /** `YYYY-MM-DD`，业务时区。服务端算好，客户端不从 `createdAt` 推。 */
  date: string;
  role: "user" | "assistant";
  /** 命中的整条文本，没有截断。要显示几行由渲染层决定。 */
  text: string;
  createdAt: string;
}

export interface MessageSearchResult {
  /**
   * 回显的查询词。
   *
   * 用户连着改了三次关键词时，三个请求可能乱序回来。拿它跟当前输入比一下，
   * 对不上就丢弃——否则界面会闪回上一次的结果。
   */
  query: string;
  hits: MessageSearchHit[];
  total: number;
  hasMore: boolean;
}

/* -------------------------------------------------------------------------- */
/* 收藏消息                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * 一条被点赞收藏的缈缈台词。
 *
 * 旧版没有独立的收藏表，收藏就是 `MessageRecord.rate === 1`
 * （`POST /api/searchMessages` 带 `{ rate: 1 }` 过滤）。新库对应
 * `MessageFeedback.rating`，`FEATURES.md` 的 CHAT-015 把它标成「重建」，
 * 要改成独立的收藏语义，所以这里按收藏建模，不叫 rate。
 */
export interface FavoriteMessage {
  /**
   * **消息 id**，不是收藏记录的 id。
   *
   * 取消收藏就是对同一条消息再点一次心，走
   * `POST /api/v1/app/messages/:id/feedback` 传 `rating: null`，用的正是这个 id。
   */
  id: string;
  conversationId: string;
  /** 缈缈说的那句话。收藏的主体永远是她的话，不是用户自己的。 */
  text: string;
  /** 触发这句话的用户输入。旧版详情页两句一起显示，没有就是 null。 */
  userInput: string | null;
  /**
   * 用户自己填的关键词。
   * 旧版这不只是标签：命中关键词的收藏会被重新塞回提示词参与召回。
   */
  tags: string[];
  favoritedAt: string;
}

export interface FavoriteList {
  favorites: FavoriteMessage[];
  total: number;
  hasMore: boolean;
}

/* -------------------------------------------------------------------------- */
/* 对话图库                                                                    */
/* -------------------------------------------------------------------------- */

export type ImageGallerySource = "all" | "assistant" | "user";

/** 对话里实际发送过的一张图。同一条消息可以有多条。 */
export interface GalleryImage {
  id: string;
  messageId: string;
  assetId: string;
  role: "assistant" | "user";
  alt: string;
  sentAt: string;
}

export interface ImageGallery {
  images: GalleryImage[];
  total: number;
  hasMore: boolean;
}

/* -------------------------------------------------------------------------- */

const opts = (signal?: AbortSignal) => (signal ? { signal } : {});

export const fetchProfile = (signal?: AbortSignal): Promise<ProfileView> =>
  apiRequest<ProfileView>("/api/v1/app/me/profile", opts(signal));

/** 改资料。返回服务端实际改了哪些键。 */
export const patchProfile = (patch: ProfilePatch): Promise<{ changed: string[] }> =>
  apiRequest<{ changed: string[] }>("/api/v1/app/me/profile", {
    method: "PATCH",
    body: patch,
  });

/** 改设置。返回服务端实际改了哪些键。 */
export const patchSettings = (patch: SettingsPatch): Promise<{ changed: string[] }> =>
  apiRequest<{ changed: string[] }>("/api/v1/app/me/settings", {
    method: "PATCH",
    body: patch,
  });

export const fetchRelationship = (signal?: AbortSignal): Promise<RelationshipView> =>
  apiRequest<RelationshipView>("/api/v1/app/me/relationship", opts(signal));

/**
 * 回忆点滴第一层：日记时间线。
 *
 * `cursor` 是**已经翻过多少条**（offset 语义，名字沿用其余列表的叫法）。
 * 不传就是第一页。
 */
export const fetchMemories = (signal?: AbortSignal, cursor?: number): Promise<MemoryCenter> =>
  apiRequest<MemoryCenter>(
    cursor === undefined
      ? "/api/v1/app/me/memories"
      : `/api/v1/app/me/memories?cursor=${cursor}`,
    opts(signal),
  );

/**
 * 回忆点滴第二层：那一天的心情日记 + 当天的对话列表。
 *
 * 一次拿全两块，因为**这一页必须能被直接访问和刷新**。从上一页把日记捏在
 * 手里传过来的话，刷新就成了空白页——而每条路由都要能被分享出去。
 *
 * `date` 是 `YYYY-MM-DD`，来自上一层列表里那条日记的 `date` 字段，
 * 不在客户端算。客户端算「今天」需要知道业务时区，那是服务端的事。
 */
export const fetchDay = (date: string, signal?: AbortSignal): Promise<DayView> =>
  apiRequest<DayView>(`/api/v1/app/me/days/${encodeURIComponent(date)}`, opts(signal));

/**
 * 回忆点滴第三层：一段对话的正文与它的记忆碎片。
 *
 * ⚠ 传的是 `DayConversation.id`（记忆碎片 id），**不是会话 id**。见那个类型的注释。
 */
export const fetchConversationMemory = (
  id: string,
  signal?: AbortSignal,
): Promise<ConversationDetail> =>
  apiRequest<ConversationDetail>(
    `/api/v1/app/me/conversation-memories/${encodeURIComponent(id)}`,
    opts(signal),
  );

/** 重新整理一段已有的记忆碎片。actionId 在同一次重试期间必须保持不变。 */
export const regenerateConversationMemory = (
  id: string,
  actionId: string,
): Promise<PaidRegenerationResult> =>
  apiRequest<PaidRegenerationResult>(
    `/api/v1/app/me/conversation-memories/${encodeURIComponent(id)}/regenerate`,
    { method: "POST", body: { actionId }, timeoutMs: 120_000 },
  );

/** 让角色重写某一天已有的关系日记。 */
export const rewriteRelationshipDiary = (
  date: string,
  actionId: string,
): Promise<PaidRegenerationResult> =>
  apiRequest<PaidRegenerationResult>(
    `/api/v1/app/me/days/${encodeURIComponent(date)}/diary/regenerate`,
    { method: "POST", body: { actionId }, timeoutMs: 120_000 },
  );

export const fetchSettings = (signal?: AbortSignal): Promise<SettingsView> =>
  apiRequest<SettingsView>("/api/v1/app/me/settings", opts(signal));

export const fetchCostNotices = (signal?: AbortSignal): Promise<CostNoticeAcknowledgments> =>
  apiRequest<CostNoticeAcknowledgments>("/api/v1/app/me/cost-notices", opts(signal));

export const acknowledgeCostNotice = (
  notice: CostNoticeKey,
): Promise<CostNoticeAcknowledgments> =>
  apiRequest<CostNoticeAcknowledgments>(
    `/api/v1/app/me/cost-notices/${encodeURIComponent(notice)}/acknowledge`,
    { method: "POST" },
  );

export const fetchConversations = (
  signal?: AbortSignal,
  cursor?: number,
): Promise<ConversationList> =>
  apiRequest<ConversationList>(
    cursor ? `/api/v1/app/me/conversations?cursor=${cursor}` : "/api/v1/app/me/conversations",
    opts(signal),
  );

/**
 * 在自己和她的对话里搜关键词。
 *
 * 服务端只搜当前关系里的消息，双方说的都搜。范围由 Session 决定，
 * 这里同样不带任何角色参数。
 *
 * ⚠ 中文搜索走的是 ILIKE 而不是全文索引（`simple` 分词把整句中文切成一个词，
 * 对中文恒无结果）。所以**它是子串匹配，不是分词匹配**：搜「生日快乐」
 * 找不到「生日的时候很快乐」。文案上不要暗示它能理解语义。
 */
export const searchMessages = (
  q: string,
  signal?: AbortSignal,
  cursor?: number,
): Promise<MessageSearchResult> => {
  const params = new URLSearchParams({ q });
  if (cursor) params.set("cursor", String(cursor));
  return apiRequest<MessageSearchResult>(
    `/api/v1/app/me/messages/search?${params.toString()}`,
    opts(signal),
  );
};

/**
 * 换头像。
 *
 * multipart，字段名 `file`，服务端只收图片（png / jpeg / webp / gif，**不收 svg**
 * ——SVG 能带脚本，从我方域名下发就是存储型 XSS）。
 *
 * 超时抬到 60 秒：默认的 8 秒是为读接口定的，移动网络上传几 MB 的照片
 * 必然超过它，而 abort 之后服务端那半截上传还在继续，用户看到的是
 * 「失败了但下次进来头像变了」。
 *
 * 成功后会**清掉**微信头像（`Profile.avatarUrl`）：两个来源同时有值时
 * 读侧无从判断该显示哪个，以用户主动传的为准。
 */
export const uploadAvatar = (file: File): Promise<{ assetId: string; url: string | null }> => {
  const form = new FormData();
  form.append("file", file);
  return apiRequest<{ assetId: string; url: string | null }>("/api/v1/app/me/avatar", {
    body: form,
    timeoutMs: 60_000,
  });
};

/**
 * 收藏消息。
 *
 * 收藏没有独立的表，服务端读的是 `MessageFeedback.rating === 1`——
 * 就是聊天里点过心的那些。旧版对应 `POST /api/searchMessages` 带 `{ rate: 1 }`，
 * 客户端自己在请求体里塞 `characterId`；新项目**禁止客户端声明 characterId**
 * （见根 AGENTS.md），它由 Session 的 productId 反查，所以这里不带角色参数。
 *
 * 路径挂在 `/api/v1/app` 下也是硬约束：Refresh Cookie 的作用域被收窄到这个
 * 前缀，挂在外面的接口 15 分钟后只能 401。
 */
export const fetchFavorites = (
  signal?: AbortSignal,
  cursor?: number,
): Promise<FavoriteList> =>
  apiRequest<FavoriteList>(
    cursor ? `/api/v1/app/me/favorites?cursor=${cursor}` : "/api/v1/app/me/favorites",
    opts(signal),
  );

/**
 * 对话图库。归属由 Session 决定，客户端只声明要看哪一方发的图。
 */
export const fetchImageGallery = (
  source: ImageGallerySource,
  signal?: AbortSignal,
  cursor?: number,
): Promise<ImageGallery> => {
  const params = new URLSearchParams();
  if (source !== "all") params.set("source", source);
  if (cursor) params.set("cursor", String(cursor));
  const query = params.toString();
  return apiRequest<ImageGallery>(
    `/api/v1/app/me/images${query ? `?${query}` : ""}`,
    opts(signal),
  );
};
